<template>
  <slot
    :selected-variant="selectedVariant"
    :select-size="selectSize"
    :select-flavor="selectFlavor"
    :is-active-size="isActiveSize"
    :is-active-flavor="isActiveFlavor"
    :current-price="currentPrice"
    :compare-price="comparePrice"
    :display-price="displayPrice"
    :display-compare-price="displayComparePrice"
    :variant-image="variantImage"
    :hover-image="hoverImage"
    :variant-title="variantTitle"
    :variant-url="variantUrl"
    :add-to-cart="handleAddToCart"
    :loading="loading"
    :size-options="sizeOptions"
    :flavor-options="flavorOptions"
    :size-label="sizeLabel"
    :flavor-label="flavorLabel"
    :selector-items="selectorItems"
    :selector-label="selectorLabel"
    :active-handle="activeHandle"
    :active-flavor-label="activeFlavorLabel"
    :select-linked-flavor="selectLinkedFlavor"
    :is-active-handle="isActiveHandle"
  />
</template>

<script>
export default {
  name: "PlpProductCard",
};
</script>

<script setup>
import {ref, computed, watch} from "vue";
import {useStore} from "vuex";
import JSON5 from "json5";
import {formatMoney} from "@shopify/theme-currency";

const props = defineProps({
  product: {
    type: String,
    required: true,
  },
  defaultVariantId: {
    type: Number,
    default: 0,
  },
  selectorItems: {
    type: Array,
    default: () => [],
  },
  selectorLabel: {
    type: String,
    default: "",
  },
});

const store = useStore();
const loading = ref(false);

// Decode URL-encoded values (handles + as space)
const decodeEntities = str =>
  String(str)
    .replace(/&#39;/g, "'")
    .replace(/&#34;/g, '"')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

const decode = val =>
  val
    ? decodeEntities(decodeURIComponent(String(val).replace(/\+/g, " ")))
    : "";

// Parse initial product data
const initialProduct = JSON5.parse(props.product);

// Reactive product — swaps when linked flavor is selected
const activeProduct = ref(initialProduct);
const activeHandle = ref(initialProduct.handle || "");

// Reactive variants derived from active product
const variants = computed(() => activeProduct.value.variants || []);

// Identify option positions for size and flavor
const findOptionIndex = (options, test) => {
  for (let i = 0; i < options.length; i++) {
    const name = decode(options[i].name || "").toLowerCase();
    if (test(name)) return i;
  }
  return -1;
};

// Variants come from product options (any name). Flavor is driven by the
// `selector_skus` metafield (linked products), handled separately outside
// this component — it does NOT come from product options.
const sizeOptionIndex = computed(() => {
  const options = activeProduct.value.options || [];
  return options.length > 0 ? 0 : -1;
});

const flavorOptionIndex = computed(() => -1);

// Selected option values
const selectedSize = ref("");
const selectedFlavor = ref("");

// Get option key for a given index
const getOptionKey = idx => `option${idx + 1}`;

// Get option value from variant at given option index
const getVariantOptionVal = (variant, optIdx) => {
  const key = getOptionKey(optIdx);
  return decode(variant[key] || "");
};

// Initialize selections from a product's default variant
const initSelections = prod => {
  const vars = prod.variants || [];
  const defVariant =
    vars.find(v => v.id === props.defaultVariantId) ||
    vars.find(v => v.available) ||
    vars[0];

  if (!defVariant) return;

  const options = prod.options || [];
  if (options.length > 0) {
    selectedSize.value = getVariantOptionVal(defVariant, 0);
  }
};

// Init on mount
initSelections(initialProduct);

// Size abbreviation helper
const abbreviateSize = size => {
  const map = {
    "extra small": "XS",
    mini: "Mini",
    small: "S",
    medium: "M",
    large: "L",
    "extra large": "XL",
    "one size": "OS",
  };
  const lower = size.toLowerCase().trim();
  if (map[lower]) return map[lower];
  const numericMatch = lower.match(/^(\d+(?:\.\d+)?)\s*[a-z]*$/i);
  if (numericMatch) {
    return numericMatch[1];
  }
  // Multi-word: take first letter of each word, max 3 chars
  // Split on spaces, hyphens, parentheses, slashes
  const words = size.trim().split(/[\s\-\/\(\)]+/).filter(w => w.length > 0);
  if (words.length >= 2) {
    return words.slice(0, 3).map(w => w[0].toUpperCase()).join("");
  }
  return size.substring(0, 3);
};

// Flavor color map (fallback for variant-based flavors)
const flavorColorMap = {
  tuna: "#E8A87C",
  chicken: "#F5C542",
  salmon: "#FA8072",
  beef: "#8B4513",
  peanut: "#D4A574",
};

// Build option arrays
const sizeOptions = computed(() => {
  if (sizeOptionIndex.value < 0) return [];
  const optKey = getOptionKey(sizeOptionIndex.value);
  const seen = new Set();
  const options = [];

  variants.value.forEach(v => {
    const rawVal = v[optKey];
    if (!rawVal) return;
    const val = decode(rawVal);
    if (seen.has(val)) return;
    seen.add(val);
    options.push({
      value: val,
      abbrev: abbreviateSize(val),
      available: variants.value.some(
        variant =>
          decode(variant[optKey] || "") === val && variant.available,
      ),
    });
  });

  return options;
});

const flavorOptions = computed(() => {
  if (flavorOptionIndex.value < 0) return [];
  const optKey = getOptionKey(flavorOptionIndex.value);
  const seen = new Set();
  const options = [];

  variants.value.forEach(v => {
    const rawVal = v[optKey];
    if (!rawVal) return;
    const val = decode(rawVal);
    if (seen.has(val)) return;
    seen.add(val);
    const lower = val.toLowerCase();
    options.push({
      value: val,
      color: flavorColorMap[lower] || "#ccc",
      image: null,
      available: variants.value.some(
        variant =>
          decode(variant[optKey] || "") === val && variant.available,
      ),
    });
  });

  return options;
});

// Selected variant computed
const selectedVariant = computed(() => {
  return variants.value.find(v => {
    let match = true;
    if (sizeOptionIndex.value >= 0 && selectedSize.value) {
      match =
        match &&
        getVariantOptionVal(v, sizeOptionIndex.value) ===
          selectedSize.value;
    }
    if (flavorOptionIndex.value >= 0 && selectedFlavor.value) {
      match =
        match &&
        getVariantOptionVal(v, flavorOptionIndex.value) ===
          selectedFlavor.value;
    }
    return match;
  });
});

const sizeLabel = computed(() => selectedSize.value);
const flavorLabel = computed(() => selectedFlavor.value);

const currentPrice = computed(() => {
  if (selectedVariant.value) {
    return selectedVariant.value.price;
  }
  return activeProduct.value.price;
});

const comparePrice = computed(() => {
  if (selectedVariant.value) {
    return selectedVariant.value.compareAtPrice;
  }
  return activeProduct.value.compareAtPrice;
});

const displayPrice = computed(() => {
  return formatMoney(currentPrice.value);
});

const displayComparePrice = computed(() => {
  if (comparePrice.value && comparePrice.value > currentPrice.value) {
    return formatMoney(comparePrice.value);
  }
  return null;
});

// Variant image
const variantImage = computed(() => {
  const prod = activeProduct.value;
  if (selectedVariant.value) {
    if (selectedVariant.value.variantImage) {
      return selectedVariant.value.variantImage.src;
    }
    if (selectedVariant.value.productImage) {
      return selectedVariant.value.productImage.src;
    }
  }
  if (prod.featured_image) {
    return prod.featured_image.src;
  }
  return prod.images && prod.images[0] ? prod.images[0] : null;
});

// Hover image
const hoverImage = computed(() => {
  const prod = activeProduct.value;
  if (prod.images && prod.images.length > 1) {
    return prod.images[1];
  }
  return null;
});

// Variant title
const variantTitle = computed(() => {
  const prod = activeProduct.value;
  if (selectedVariant.value) {
    return (
      decode(selectedVariant.value.productTitle || "") ||
      decode(prod.title || "")
    );
  }
  return decode(prod.title || "");
});

// Product URL with variant
const variantUrl = computed(() => {
  const base = decode(activeProduct.value.url || "");
  if (selectedVariant.value) {
    return `${base}?variant=${selectedVariant.value.id}`;
  }
  return base;
});

// Actions
const selectSize = value => {
  selectedSize.value = value;
};

const selectFlavor = value => {
  selectedFlavor.value = value;
};

const isActiveSize = value => {
  return selectedSize.value === value;
};

const isActiveFlavor = value => {
  return selectedFlavor.value === value;
};

// Linked product flavor selector
const activeFlavorLabel = computed(() => {
  const current = props.selectorItems.find(
    i => i.handle === activeHandle.value,
  );
  return current?.label || "";
});

const isActiveHandle = handle => {
  return handle === activeHandle.value;
};

const selectLinkedFlavor = item => {
  if (item.handle === activeHandle.value) return;
  if (!item.product) return;

  // Swap the active product
  activeProduct.value = item.product;
  activeHandle.value = item.handle;

  // Re-init size/flavor selections for the new product
  selectedSize.value = "";
  selectedFlavor.value = "";
  initSelections(item.product);
};

const handleAddToCart = async () => {
  if (!selectedVariant.value || loading.value) return;

  loading.value = true;
  try {
    await store.dispatch("cart/addItem", {
      id: selectedVariant.value.id,
      quantity: 1,
    });
    store.dispatch("cart/show");
  } catch (err) {
    console.error("Add to cart error:", err);
  } finally {
    loading.value = false;
  }
};
</script>
